import React from 'react';
import { PageHeader } from '../components/PageHeader';
import { useSettings } from '../contexts/SettingsContext';
import { HelpCircle, Eye, User, Tag, Coffee, MapPin, Phone } from 'lucide-react';
import { Switch } from '../components/ui/switch';

export function HelpPage() {
    const { seniorMode, toggleSeniorMode } = useSettings();

    const steps = [
        {
            icon: User,
            title: 'Create an Account',
            description: 'Tap "Friend of Burston" to join or sign in. Use the demo account to try it out first.'
        },
        {
            icon: Tag,
            title: 'View Offers',
            description: 'Tap "Offers" to see current deals. Some offers are for members only - join to unlock them.'
        },
        {
            icon: Coffee,
            title: 'Collect Stamps',
            description: 'Buy hot drinks at the café and show your membership card. Collect 8 stamps for a free drink!'
        },
        {
            icon: MapPin,
            title: 'Find Us',
            description: 'Tap "Visit Us" for directions, opening times, and contact information.'
        },
    ];

    return (
        <div className="page-container" data-testid="help-page">
            <PageHeader title="Help" showBack />
            
            <div className="px-4 py-4">
                {/* Accessibility Settings */}
                <div className="card-base mb-6" data-testid="accessibility-settings">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                                <Eye className="w-6 h-6 text-primary" />
                            </div>
                            <div>
                                <p className="font-bold text-foreground">Large Text Mode</p>
                                <p className="text-muted-foreground text-sm">Make text bigger and easier to read</p>
                            </div>
                        </div>
                        <Switch
                            checked={seniorMode}
                            onCheckedChange={toggleSeniorMode}
                            data-testid="help-senior-mode-toggle"
                        />
                    </div>
                </div>

                {/* How to use */}
                <h2 className="text-xl font-bold text-foreground mb-4">How to Use This App</h2>
                
                <div className="space-y-4" data-testid="help-steps">
                    {steps.map((step, index) => (
                        <div key={index} className="card-base">
                            <div className="flex items-start gap-4">
                                <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                                    <span className="text-white font-bold">{index + 1}</span>
                                </div>
                                <div>
                                    <h3 className="font-bold text-foreground mb-1">{step.title}</h3>
                                    <p className="text-muted-foreground">{step.description}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Navigation Help */}
                <h2 className="text-xl font-bold text-foreground mt-8 mb-4">Getting Around</h2>
                
                <div className="card-base bg-primary/5">
                    <p className="text-muted-foreground mb-4">
                        Use the buttons at the bottom of the screen to navigate:
                    </p>
                    <ul className="space-y-2 text-muted-foreground">
                        <li className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-primary" />
                            <strong className="text-foreground">Home</strong> - Quick links and announcements
                        </li>
                        <li className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-primary" />
                            <strong className="text-foreground">Offers</strong> - Current deals and discounts
                        </li>
                        <li className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-primary" />
                            <strong className="text-foreground">What's On</strong> - Events and workshops
                        </li>
                        <li className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-primary" />
                            <strong className="text-foreground">Café</strong> - Menu and stamp card
                        </li>
                        <li className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-primary" />
                            <strong className="text-foreground">More</strong> - Account, tips, and settings
                        </li>
                    </ul>
                </div>

                {/* Need Help */}
                <div className="mt-8 card-base bg-accent/10 border-2 border-accent/30" data-testid="help-contact">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-accent flex items-center justify-center flex-shrink-0">
                            <HelpCircle className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <h3 className="font-bold text-foreground mb-1">Need More Help?</h3>
                            <p className="text-muted-foreground">
                                Just ask at Customer Service - our friendly team are happy to assist.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Contact */}
                <a 
                    href="tel:01727832444" 
                    className="mt-4 card-base flex items-center justify-center gap-2 text-primary font-bold"
                    data-testid="help-phone"
                >
                    <Phone className="w-5 h-5" />
                    <span>Call Us: 01727 832444</span>
                </a>
            </div>
        </div>
    );
}

export default HelpPage;
